import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { generateGUID } from '../utils/guid';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const UserContext = createContext();

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) throw new Error('useUser must be used within a UserProvider');
    return context;
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const navigate = useNavigate();
    const [isUserReady, setIsUserReady] = useState(false);
    const [modules, setModules] = useState({});
    const [roles, setRoles] = useState({});

    const refreshUser = useCallback(async () => {
        try {
            const res = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/auth/me`);
            if (!res.ok) throw new Error("Не удалось обновить пользователя");
    
            const data = await res.json();
            setUser(prev => ({ ...prev, user: data.user }));
            setIsUserReady(true);
        } catch (err) {
            console.error("Ошибка при обновлении пользователя:", err);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${user?.accessToken}`,
                },
                credentials: "include",
            });
        } catch (err) {
            console.error("Ошибка при logout:", err);
        } finally {
            // Всегда выполняем очистку и переход, даже при ошибке запроса
            localStorage.removeItem("user");
            setUser(null);
            setIsAuthenticated(false);
            setIsUserReady(false); 
            
            // Добавим небольшую задержку перед навигацией
            setTimeout(() => {
                navigate("/login", { replace: true });
            }, 100);
        }
    }, [user, navigate]);

    const login = useCallback(async (username, password) => {
        // const deviceId = getDeviceId();

        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ username, password }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Ошибка авторизации');
        }

        const data = await response.json();
        localStorage.setItem("user", JSON.stringify(data));
        setUser(data);
        setIsAuthenticated(true);
        setIsUserReady(true);
        
        try {
            await refreshUser();
        } catch (error) {
            console.error("Ошибка при обновлении данных пользователя:", error);
        }
        
        return data;
    }, [refreshUser]);

    // Проверка валидности токена при старте
    useEffect(() => {
        const checkSession = async () => {
            const storedUser = localStorage.getItem("user");
            if (!storedUser) return;

            try {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
                setIsAuthenticated(true);
                
                const res = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/auth/me`);
                if (!res.ok) throw new Error("Токен невалиден");

                const data = await res.json();
                setUser(prev => ({ 
                    ...prev, 
                    user: {
                        ...data.user,
                        permissions: data.user.permissions,
                        role: data.user.role
                    } 
                }));
                setIsUserReady(true);
            } catch (err) {
                console.warn("❌ Ошибка валидации /me:", err);
                localStorage.removeItem("user");
                setUser(null);
                setIsAuthenticated(false);
                navigate("/login", { replace: true });
            }
        };

        checkSession();
    }, [navigate]);

    useEffect(() => {
        if (!isAuthenticated) return;
        
        const fetchModules = async () => {
            try {
                const res = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/modules`, {}, logout);
                const data = await res.json();
                setModules(data);
            } catch (error) {
                console.error("Ошибка загрузки модулей:", error);
            }
        };

        const fetchRoles = async () => {
            try {
                const res = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/roles`, {}, logout);
                const data = await res.json();
                setRoles(data);
            } catch (error) {
                console.error("Ошибка загрузки ролей:", error);
            }
        };

        fetchModules();
        fetchRoles();
    }, [isAuthenticated, logout]);

    const getModule = useCallback((moduleName) => {
        return modules[moduleName] || null;
    }, [modules]);

    const getRole = useCallback((roleName) => {
        return roles[roleName] || null;
    }, [roles]);

    const getAllRoles = useCallback(() => {
        return roles;
    }, [roles]);
    
    const hasPermission = useCallback((moduleName, action) => {
        const currentUser = user?.user;
        if (!currentUser) return false;
        if (currentUser.permissions === "*") return true;
    
        const modulePerm = currentUser.permissions?.find(perm => perm.module === moduleName);
        return modulePerm?.actions?.includes(action) || false;
    }, [user]);

    const hasAnyPermission = useCallback((moduleName, actions) => {
        const perm = user?.user?.permissions?.find(p => p.module === moduleName);
        return !!perm && actions.some(action => perm.actions?.includes(action));
    }, [user]);
    
    const hasAllPermissions = useCallback((moduleName, actions) => {
        const perm = user?.user?.permissions?.find(p => p.module === moduleName);
        return !!perm && actions.every(action => perm.actions?.includes(action));
    }, [user]);
    
    const getAllowedModules = useCallback(() => {
        const perms = user?.user?.permissions || [];
        return Array.isArray(perms) ? perms.map(p => p.module) : [];
    }, [user]);
    
    const hasRole = useCallback((roleName) => {
        const currentUser = user?.user;
        if (!currentUser) return false;
        return currentUser.role === roleName;
    }, [user]);

    const hasAnyRole = useCallback((roleNames) => {
        const currentUser = user?.user;
        if (!currentUser) return false;
        return Array.isArray(roleNames) && roleNames.includes(currentUser.role);
    }, [user]);

    return (
        <UserContext.Provider value={{
            user,
            setUser,
            isAuthenticated,
            setIsAuthenticated,
            login,
            logout,
            refreshUser,
            hasAnyPermission,
            hasAllPermissions,
            getAllowedModules,
            hasPermission,
            getModule,
            modules,
            isUserReady,
            getRole,
            getAllRoles,
            hasRole,
            hasAnyRole
        }}>
            {children}
        </UserContext.Provider>
    );
};

const getDeviceId = () => {
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
        deviceId = generateGUID();
        localStorage.setItem("deviceId", deviceId);
    }
    return deviceId;
};
